import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Switch, Popconfirm, message, Space, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import productService from '../services/productService';

const { Text } = Typography;

const SUPPORTED_TYPE_OPTIONS = [
  { value: 'License', label: 'License' },
  { value: 'Service', label: 'Service' },
];

const ProductsSettings = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await productService.list({ includeInactive: true });
      setProducts(res.data?.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ supportedTypes: [], isActive: true });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      supportedTypes: record.supportedTypes || [],
      isActive: record.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await productService.update(editing._id, values);
        message.success('Product updated');
      } else {
        await productService.create(values);
        message.success('Product added');
      }
      setModalOpen(false);
      fetchProducts();
    } catch (err) {
      if (err?.errorFields) return; // validation handled inline
      message.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await productService.remove(record._id);
      message.success('Product deleted');
      fetchProducts();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Supported Types',
      dataIndex: 'supportedTypes',
      key: 'supportedTypes',
      render: (types) =>
        types && types.length > 0
          ? types.map((t) => <Tag key={t} color={t === 'License' ? 'blue' : 'green'}>{t}</Tag>)
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active) => (active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Delete this product?"
            description="Deals already tagged with it will keep the reference."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <Text strong className="text-lg">Product Catalog</Text>
          <div className="text-sm text-gray-500">
            Products defined here appear in the deal-tagging dropdown for all users.
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Product
        </Button>
      </div>

      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={products}
        columns={columns}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No products yet — click "Add Product" to create the catalog.' }}
      />

      <Modal
        title={editing ? 'Edit Product' : 'Add Product'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Save' : 'Add'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Enter a product name' }]}
          >
            <Input placeholder="e.g. Qlik, Power BI, Aryabhata" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="supportedTypes"
            label="Supported Types"
            tooltip="Pick the types this product is sold as. Leave empty if the product has no License/Service split."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="License, Service (optional)"
              options={SUPPORTED_TYPE_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductsSettings;
